import { Link, useParams } from "react-router-dom";
import Seo from "@/components/feature/Seo";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import VerdictBadge from "@/components/base/VerdictBadge";
import Tag from "@/components/base/Tag";
import CaseCard from "@/components/feature/CaseCard";
import { getCaseBySlug, getRelatedCases, type TimelineType } from "@/mocks/cases";
import { getGuidesByIds } from "@/mocks/guides";
import { getFactCheckById } from "@/mocks/factChecks";
import { siteInfo } from "@/mocks/site";

const timelineStyle: Record<TimelineType, { dot: string; badge: string }> = {
  爆料: { dot: "bg-primary-500", badge: "bg-primary-100 text-primary-800" },
  发酵: { dot: "bg-secondary-500", badge: "bg-secondary-100 text-secondary-900" },
  官方回应: { dot: "bg-accent-500", badge: "bg-accent-100 text-accent-900" },
  核验: { dot: "bg-foreground-700", badge: "bg-background-200 text-foreground-800" },
  反转: { dot: "bg-primary-600", badge: "bg-primary-100 text-primary-800" },
};

export default function CaseDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const item = slug ? getCaseBySlug(slug) : undefined;

  if (!item) {
    return (
      <section className="w-full bg-background-50 py-20 md:py-28">
        <div className="mx-auto w-full max-w-[1240px] px-4 text-center md:px-6">
          <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-500" aria-hidden="true">
            <i className="ri-file-unknow-line text-2xl" />
          </span>
          <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">未找到该案例</h1>
          <p className="mt-3 text-sm text-foreground-600">这个案例可能已被合并或调整了链接地址。</p>
          <Link
            to="/cases"
            className="mt-6 inline-flex items-center gap-1.5 rounded-md bg-primary-500 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
          >
            <i className="ri-arrow-left-line text-base" aria-hidden="true" />
            返回事件案例库
          </Link>
        </div>
      </section>
    );
  }

  const relatedCases = getRelatedCases(item, 3);
  const relatedGuides = getGuidesByIds(item.relatedGuideIds);
  const relatedFacts = item.relatedFactCheckIds
    .map((id) => getFactCheckById(id))
    .filter((fact): fact is NonNullable<typeof fact> => Boolean(fact));

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "NewsArticle",
      headline: item.title,
      description: item.summary,
      datePublished: item.publishDate,
      dateModified: item.updateDate,
      inLanguage: "zh-CN",
      articleSection: item.category,
      url: `${siteInfo.url}/cases/${item.slug}`,
      author: { "@type": "Organization", name: siteInfo.name, url: siteInfo.url },
      publisher: { "@type": "Organization", name: siteInfo.name, url: siteInfo.url },
      image: item.cover,
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: [
        {
          "@type": "Question",
          name: `${item.title}的核验结论是什么？`,
          acceptedAnswer: { "@type": "Answer", text: item.conclusion },
        },
        {
          "@type": "Question",
          name: "这起事件的官方回应有哪些？",
          acceptedAnswer: {
            "@type": "Answer",
            text: item.officialResponses
              .map((res) => `${res.org}（${res.date}）：${res.content}`)
              .join("；"),
          },
        },
      ],
    },
  ];

  return (
    <>
      <Seo
        title={`${item.title}｜事件核验与官方回应 - 棱镜求证`}
        description={`${item.summary.slice(0, 80)}。核验结论：${item.verdict}，更新于 ${item.updateDate}。`}
        keywords={`${item.category},社会爆料反转,官方回应,真相核验`}
        path={`/cases/${item.slug}`}
        ogType="article"
        jsonLd={jsonLd}
      />

      <section className="w-full border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-[1240px] px-4 py-8 md:px-6 md:py-12">
          <Breadcrumbs
            items={[
              { label: "事件案例库", path: "/cases" },
              { label: item.title },
            ]}
          />

          <div className="mt-5 flex flex-wrap items-center gap-2">
            <Tag tone="secondary" icon="ri-price-tag-3-line">
              {item.category}
            </Tag>
            <VerdictBadge verdict={item.status} />
            <span className="flex items-center gap-1.5 text-xs text-foreground-500">
              <i className="ri-map-pin-line text-sm" aria-hidden="true" />
              {item.region}
            </span>
          </div>

          <h1 className="mt-4 max-w-4xl font-heading text-2xl font-black leading-snug text-foreground-950 md:text-4xl">
            {item.title}
          </h1>
          <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-700 md:text-base">
            {item.summary}
          </p>

          <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-foreground-500 md:text-sm">
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line text-sm" aria-hidden="true" />
              首次发布 {item.publishDate}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-refresh-line text-sm" aria-hidden="true" />
              最近更新 {item.updateDate}
            </span>
            <span className="flex items-center gap-1.5 text-accent-700">
              <i className="ri-fire-line text-sm" aria-hidden="true" />
              热度 {item.heat.toLocaleString()}
            </span>
          </div>
        </div>
      </section>

      <section className="w-full bg-background-50 py-10 md:py-14">
        <div className="mx-auto grid w-full max-w-[1240px] grid-cols-1 gap-10 px-4 md:px-6 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <div className="w-full overflow-hidden rounded-lg border border-background-200">
              <div className="h-56 w-full bg-background-200 md:h-80">
                <img
                  src={item.cover}
                  alt={item.coverAlt}
                  title={`${item.title} - 事件核验画面`}
                  className="h-full w-full object-cover object-top"
                />
              </div>
            </div>

            <article className="mt-10">
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">事件时间线</h2>
              <p className="mt-2 text-sm text-foreground-600">
                按时间顺序还原信息发酵过程，注意每个节点的时间与来源。
              </p>

              <ol className="mt-6 space-y-0">
                {item.timeline.map((node, index) => {
                  const style = timelineStyle[node.type];
                  const isLast = index === item.timeline.length - 1;
                  return (
                    <li key={`${node.date}-${index}`} className="relative flex gap-4 pb-6 last:pb-0">
                      <div className="flex flex-col items-center">
                        <span className={`mt-1 h-3 w-3 shrink-0 rounded-full ${style.dot}`} aria-hidden="true" />
                        {!isLast ? <span className="mt-1 w-px flex-1 bg-background-300" aria-hidden="true" /> : null}
                      </div>
                      <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className={`rounded-md px-2 py-0.5 text-xs font-semibold ${style.badge}`}>
                            {node.type}
                          </span>
                          <span className="text-xs text-foreground-500">{node.date}</span>
                        </div>
                        <h3 className="mt-2 font-heading text-base font-bold text-foreground-950">
                          {node.title}
                        </h3>
                        <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{node.desc}</p>
                      </div>
                    </li>
                  );
                })}
              </ol>
            </article>

            <article className="mt-10">
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">官方回应汇总</h2>
              <div className="mt-5 space-y-4">
                {item.officialResponses.map((res) => (
                  <div key={`${res.org}-${res.date}`} className="rounded-lg border border-background-200 bg-background-100 p-5">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="flex items-center gap-2 font-heading text-sm font-bold text-foreground-950">
                        <span className="flex h-7 w-7 items-center justify-center rounded-md bg-accent-100 text-accent-800" aria-hidden="true">
                          <i className="ri-megaphone-line text-sm" />
                        </span>
                        {res.org}
                      </span>
                      <span className="text-xs text-foreground-500">{res.date}</span>
                    </div>
                    <p className="mt-3 text-sm leading-relaxed text-foreground-700">{res.content}</p>
                  </div>
                ))}
              </div>
            </article>

            <article className="mt-10">
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">核验结论</h2>
              <div className="mt-5 rounded-lg border-l-4 border-primary-500 bg-primary-50 p-5 md:p-6">
                <div className="flex items-center gap-2">
                  <VerdictBadge verdict={item.verdict} />
                  <span className="text-xs text-foreground-500">核验时间 {item.updateDate}</span>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-foreground-800 md:text-base">
                  {item.conclusion}
                </p>
              </div>
            </article>

            <article className="mt-10">
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">关键事实</h2>
              <ul className="mt-5 space-y-3">
                {item.keyFacts.map((fact) => (
                  <li key={fact} className="flex items-start gap-3 rounded-md border border-background-200 bg-background-50 p-4">
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-secondary-100 text-secondary-800" aria-hidden="true">
                      <i className="ri-check-line text-xs" />
                    </span>
                    <span className="text-sm leading-relaxed text-foreground-700">{fact}</span>
                  </li>
                ))}
              </ul>
            </article>

            <article className="mt-10">
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">参考来源</h2>
              <ul className="mt-4 flex flex-wrap gap-2">
                {item.sources.map((source) => (
                  <li key={source}>
                    <Tag tone="outline" icon="ri-links-line">
                      {source}
                    </Tag>
                  </li>
                ))}
              </ul>
            </article>
          </div>

          <aside className="lg:col-span-4">
            <div className="lg:sticky lg:top-28 space-y-6">
              <div className="rounded-lg border border-background-200 bg-background-100 p-5">
                <h2 className="font-heading text-base font-bold text-foreground-950">核验速览</h2>
                <dl className="mt-4 space-y-3 text-sm">
                  <div className="flex items-center justify-between gap-3">
                    <dt className="text-foreground-500">核验状态</dt>
                    <dd><VerdictBadge verdict={item.status} /></dd>
                  </div>
                  <div className="flex items-center justify-between gap-3 border-t border-background-200 pt-3">
                    <dt className="text-foreground-500">结论</dt>
                    <dd className="font-medium text-foreground-900">{item.verdict}</dd>
                  </div>
                  <div className="flex items-center justify-between gap-3 border-t border-background-200 pt-3">
                    <dt className="text-foreground-500">事件分类</dt>
                    <dd className="font-medium text-foreground-900">{item.category}</dd>
                  </div>
                  <div className="flex items-center justify-between gap-3 border-t border-background-200 pt-3">
                    <dt className="text-foreground-500">涉及地区</dt>
                    <dd className="font-medium text-foreground-900">{item.region}</dd>
                  </div>
                  <div className="flex items-center justify-between gap-3 border-t border-background-200 pt-3">
                    <dt className="text-foreground-500">最近更新</dt>
                    <dd className="font-medium text-foreground-900">{item.updateDate}</dd>
                  </div>
                </dl>
              </div>

              {relatedFacts.length > 0 ? (
                <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                  <h2 className="font-heading text-base font-bold text-foreground-950">相关辟谣结论</h2>
                  <ul className="mt-3 space-y-3">
                    {relatedFacts.map((fact) => (
                      <li key={fact.id} className="border-t border-background-200 pt-3 first:border-0 first:pt-0">
                        <div className="flex items-center justify-between gap-2">
                          <Tag tone={fact.verdict === "谣言" ? "primary" : "secondary"}>{fact.verdict}</Tag>
                          <span className="text-xs text-foreground-500">{fact.date}</span>
                        </div>
                        <p className="mt-2 text-sm leading-snug text-foreground-800">{fact.title}</p>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/fact-check"
                    className="mt-4 inline-flex items-center gap-1 text-xs font-medium text-primary-600 transition-colors hover:text-primary-700"
                  >
                    查看全部核验
                    <i className="ri-arrow-right-s-line text-sm" aria-hidden="true" />
                  </Link>
                </div>
              ) : null}

              {relatedGuides.length > 0 ? (
                <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                  <h2 className="font-heading text-base font-bold text-foreground-950">延伸阅读</h2>
                  <ul className="mt-3 space-y-3">
                    {relatedGuides.map((guide) => (
                      <li key={guide.id}>
                        <Link
                          to={`/guide/${guide.slug}`}
                          className="group flex items-start gap-2 text-sm font-medium text-foreground-800 transition-colors hover:text-accent-800"
                        >
                          <span className="mt-1 flex h-4 w-4 shrink-0 items-center justify-center text-accent-500" aria-hidden="true">
                            <i className="ri-book-open-line text-sm" />
                          </span>
                          <span className="leading-snug">{guide.title}</span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>
          </aside>
        </div>
      </section>

      <section className="w-full border-t border-background-200 bg-background-100 py-12 md:py-16">
        <div className="mx-auto w-full max-w-[1240px] px-4 md:px-6">
          <div className="mb-6 flex items-center justify-between">
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">相关案例</h2>
            <Link
              to="/cases"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 transition-colors hover:text-primary-700"
            >
              全部案例
              <i className="ri-arrow-right-line text-base" aria-hidden="true" />
            </Link>
          </div>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {relatedCases.map((related) => (
              <CaseCard key={related.id} item={related} />
            ))}
          </div>
        </div>
      </section>
    </>
  );
}